import React, { useEffect, useMemo, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, parseISO } from "date-fns";
import { Trash2, Pencil, X, Check, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const EMPTY_EVENT = { date: "", end_date: "", type: "off_day", title: "", title_zh: "", notes: "" };

const TONES = {
  off_day:   { bar: "bg-rose-400/85",       text: "text-rose-50",       dot: "bg-rose-400" },
  activity:  { bar: "bg-[hsl(var(--primary))]/85", text: "text-white",  dot: "bg-[hsl(var(--primary))]" },
  holiday:   { bar: "bg-amber-400/85",      text: "text-amber-950",     dot: "bg-amber-400" },
};

function MonthGrid({ events, holidays, monthStart, onSelectEvent, onDayClick, editingId }) {
  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(monthStart), { weekStartsOn: 0 });
    const gridEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [monthStart]);

  // Convert events + holidays into a unified list with parsed dates.
  const allEvents = useMemo(() => {
    const out = [];
    for (const e of events || []) {
      out.push({
        id: e.id,
        kind: e.type, // "off_day" | "activity"
        title: e.title,
        title_zh: e.title_zh,
        start: parseISO(e.date),
        end: parseISO(e.end_date || e.date),
        raw: e,
      });
    }
    for (const h of holidays || []) {
      out.push({
        id: `holiday-${h.date}`,
        kind: "holiday",
        title: h.title,
        title_zh: h.title_zh,
        start: parseISO(h.date),
        end: parseISO(h.date),
        raw: h,
      });
    }
    return out;
  }, [events, holidays]);

  function eventsForDay(day) {
    return allEvents.filter(e => day >= e.start && day <= e.end);
  }

  return (
    <div data-testid="admin-cal-grid" className="bg-white border border-[hsl(var(--border))] rounded-2xl overflow-hidden">
      <div className="grid grid-cols-7 bg-[hsl(var(--muted))]/40 border-b border-[hsl(var(--border))]">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} className="py-2 text-center text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, idx) => {
          const inMonth = isSameMonth(day, monthStart);
          const isToday = isSameDay(day, new Date());
          const dayEvents = eventsForDay(day);
          return (
            <button type="button"
              key={day.toISOString()}
              onClick={() => onDayClick?.(day)}
              data-testid={`cal-grid-day-${format(day, "yyyy-MM-dd")}`}
              className={`relative text-left min-h-[88px] p-1.5 border-b border-r border-[hsl(var(--border))]/60 transition-colors ${!inMonth ? "bg-[hsl(var(--muted))]/20" : "hover:bg-[hsl(var(--primary))]/[0.04]"} ${(idx + 1) % 7 === 0 ? "border-r-0" : ""}`}>
              <div className={`text-[11px] font-medium leading-none ${!inMonth ? "text-[hsl(var(--muted-foreground))]/60" : isToday ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--foreground))]"} flex items-center justify-between`}>
                <span className={isToday ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-[hsl(var(--primary))] text-white text-[10px]" : ""}>{format(day, "d")}</span>
              </div>
              <div className="mt-1 space-y-0.5">
                {dayEvents.slice(0, 3).map((e) => {
                  const tone = TONES[e.kind] || TONES.activity;
                  const isStart = isSameDay(day, e.start);
                  const isEnd = isSameDay(day, e.end);
                  return (
                    <div
                      key={e.id}
                      onClick={(ev)=>{ ev.stopPropagation(); if (e.kind !== "holiday") onSelectEvent?.(e.raw); }}
                      title={`${e.title}${e.title_zh ? ` · ${e.title_zh}` : ""}${e.start.getTime() !== e.end.getTime() ? ` (${format(e.start, "MMM d")} → ${format(e.end, "MMM d")})` : ""}`}
                      className={`text-[10px] px-1.5 py-0.5 truncate ${tone.bar} ${tone.text} ${isStart ? "rounded-l" : ""} ${isEnd ? "rounded-r" : ""} ${!isStart ? "-ml-1.5 pl-2" : ""} ${!isEnd ? "-mr-1.5 pr-2" : ""} ${editingId && e.raw.id === editingId ? "ring-2 ring-offset-1 ring-[hsl(var(--primary))]" : ""} cursor-pointer`}
                      data-testid={`cal-grid-swatch-${e.id}-${format(day, "yyyy-MM-dd")}`}>
                      {isStart ? (e.title || "") : <span className="opacity-0 select-none">·</span>}
                    </div>
                  );
                })}
                {dayEvents.length > 3 && <div className="text-[9px] text-[hsl(var(--muted-foreground))] italic">+{dayEvents.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CalendarPanel() {
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [draft, setDraft] = useState(EMPTY_EVENT);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [monthStart, setMonthStart] = useState(startOfMonth(new Date()));

  async function load() {
    try {
      const today = new Date();
      const start = format(today, "yyyy-MM-dd");
      const end = format(new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()), "yyyy-MM-dd");
      const { data } = await api.get(`/calendar?start=${start}&end=${end}`);
      setHolidays(data?.holidays || []);
      setEvents(data?.events || []);
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  function resetForm() { setDraft(EMPTY_EVENT); setEditingId(null); }

  function startEdit(ev) {
    setEditingId(ev.id);
    setDraft({
      date: ev.date || "",
      end_date: ev.end_date || "",
      type: ev.type || "off_day",
      title: ev.title || "",
      title_zh: ev.title_zh || "",
      notes: ev.notes || "",
    });
    // Scroll the form into view on mobile
    setTimeout(() => document.querySelector('[data-testid="admin-calendar-form"]')?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  }

  function startQuickAddForDay(day) {
    if (editingId) return;
    setDraft({ ...EMPTY_EVENT, date: format(day, "yyyy-MM-dd") });
  }

  async function submit(e) {
    e.preventDefault();
    if (!draft.date || !draft.title) { toast.error("Date and title are required"); return; }
    if (draft.end_date && draft.end_date < draft.date) { toast.error("End date cannot be before start date"); return; }
    setSaving(true);
    try {
      const payload = { ...draft };
      if (!payload.end_date) delete payload.end_date;
      if (editingId) {
        await api.patch(`/calendar/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/calendar/events", payload);
        toast.success(payload.type === "off_day" ? "Off-day added" : "Activity added");
      }
      resetForm();
      load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setSaving(false); }
  }

  async function del(id) {
    if (!window.confirm("Remove this event from the calendar?")) return;
    try { await api.delete(`/calendar/events/${id}`); toast.success("Removed"); if (editingId === id) resetForm(); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="admin-calendar-panel" className="space-y-6">
      {/* Month navigator + grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[hsl(var(--primary))]" />
            <h3 className="font-display text-xl tracking-tight">{format(monthStart, "MMMM yyyy")}</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={()=>setMonthStart(addMonths(monthStart, -1))} data-testid="admin-cal-prev-month" className="rounded-full h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={()=>setMonthStart(startOfMonth(new Date()))} data-testid="admin-cal-today" className="rounded-full h-8 text-xs px-3">Today</Button>
            <Button variant="outline" size="sm" onClick={()=>setMonthStart(addMonths(monthStart, 1))} data-testid="admin-cal-next-month" className="rounded-full h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
        <MonthGrid
          events={events}
          holidays={holidays}
          monthStart={monthStart}
          editingId={editingId}
          onSelectEvent={startEdit}
          onDayClick={startQuickAddForDay}
        />
        <ul className="flex flex-wrap items-center gap-4 text-[11px] text-[hsl(var(--muted-foreground))] mt-3" data-testid="admin-cal-legend">
          <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-rose-400" />Studio off-day</li>
          <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-[hsl(var(--primary))]" />Studio activity</li>
          <li className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-amber-400" />National holiday</li>
          <li className="italic">· Click any day to start a new event · Click a swatch to edit</li>
        </ul>
      </div>

      {/* Form + lists */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <form onSubmit={submit} className="lg:col-span-5 bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-4 h-fit" data-testid="admin-calendar-form">
          <div className="flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{editingId ? "Edit event" : "Add a calendar event"}</h3>
            {editingId && <Button type="button" variant="ghost" size="sm" onClick={resetForm} data-testid="admin-cal-cancel-edit"><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Type</Label>
            <Select value={draft.type} onValueChange={(v)=>setDraft({...draft, type: v})}>
              <SelectTrigger data-testid="admin-cal-type" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off_day" data-testid="admin-cal-type-off">Studio off-day (closed)</SelectItem>
                <SelectItem value="activity" data-testid="admin-cal-type-activity">School activity</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Start date</Label>
              <Input type="date" required value={draft.date} onChange={(e)=>setDraft({...draft, date: e.target.value})} data-testid="admin-cal-date" className="mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">End date <span className="italic text-[hsl(var(--muted-foreground))]/70">(optional · for multi-day)</span></Label>
              <Input type="date" value={draft.end_date || ""} min={draft.date || undefined} onChange={(e)=>setDraft({...draft, end_date: e.target.value})} data-testid="admin-cal-end-date" className="mt-2" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Title (English)</Label>
            <Input required value={draft.title} onChange={(e)=>setDraft({...draft, title: e.target.value})} data-testid="admin-cal-title" className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Title (中文)</Label>
            <Input value={draft.title_zh} onChange={(e)=>setDraft({...draft, title_zh: e.target.value})} data-testid="admin-cal-title-zh" className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Notes</Label>
            <Textarea rows={2} value={draft.notes} onChange={(e)=>setDraft({...draft, notes: e.target.value})} data-testid="admin-cal-notes" className="mt-2" />
          </div>
          <Button type="submit" disabled={saving} data-testid="admin-cal-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
            {saving ? "…" : (editingId ? <><Check className="w-4 h-4 mr-1.5" />Save changes</> : "Add to calendar")}
          </Button>
        </form>

        <div className="lg:col-span-7 space-y-6">
          <div>
            <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] mb-3">Upcoming studio events · {events.length}</h3>
            <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
              {events.map((e) => (
                <li key={e.id} data-testid={`admin-cal-event-${e.id}`} className={`p-4 flex items-start gap-4 justify-between ${editingId === e.id ? "bg-[hsl(var(--primary))]/5" : ""}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                      <span className="text-[hsl(var(--muted-foreground))]">{e.date}{e.end_date && e.end_date !== e.date ? ` → ${e.end_date}` : ""}</span>
                      <span>·</span>
                      <span className={e.type === "off_day" ? "text-rose-600" : "text-[hsl(var(--primary))]"}>
                        {e.type === "off_day" ? "OFF-DAY" : "ACTIVITY"}
                      </span>
                    </div>
                    <div className="font-display text-lg tracking-tight mt-1">{e.title}</div>
                    {e.title_zh && <div className="text-sm text-[hsl(var(--muted-foreground))]">{e.title_zh}</div>}
                    {e.notes && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{e.notes}</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={()=>startEdit(e)} data-testid={`admin-cal-edit-${e.id}`} title="Edit"><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={()=>del(e.id)} data-testid={`admin-cal-delete-${e.id}`} className="text-[hsl(var(--destructive))]" title="Delete"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </li>
              ))}
              {events.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">No upcoming off-days or activities yet.</li>}
            </ul>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] mb-3">Auto-loaded national holidays (CN) · {holidays.length}</h3>
            <div className="bg-white border border-[hsl(var(--border))] rounded-2xl max-h-72 overflow-y-auto">
              <ul className="divide-y divide-[hsl(var(--border))]/60" data-testid="admin-cal-holidays">
                {holidays.map((h, i) => (
                  <li key={`${h.date}-${i}`} className="p-3 flex items-center justify-between text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">{h.date}</span>
                    <span className="text-[hsl(var(--foreground))]">{h.title} · {h.title_zh}</span>
                  </li>
                ))}
                {holidays.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">—</li>}
              </ul>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">These are hard-coded for 2026–2027. Need a date adjusted? Add a &quot;Studio off-day&quot; above to extend a holiday window.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
